import type { AccountUsage, LimitWindow, UsageReport } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, get } from "@/api/client.ts";
import { cn } from "@/lib/cn.ts";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { Card, Spinner } from "@/ui/primitives.tsx";

const usageApi = {
  report: () => get<UsageReport>("/api/usage"),
  setRenewal: (id: string, renewsAt: string | null) => api<void>(`/api/usage/accounts/${id}/renewal`, { method: "PUT", json: { renewsAt } }),
};
const COLORS: Record<string, string> = { claude: "#D77655", codex: "#10a37f", gemini: "#6f7cf0" };

/** What each provider account has left (the providers' own five-hour and weekly windows), the renewal dates, who used what, and what failed. */
export function UsageSettings({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["usage"], queryFn: usageApi.report, refetchInterval: 60_000 });
  if (q.isLoading) return <Spinner />;
  const r = q.data;
  if (!r) return null;
  return (
    <div className="space-y-4">
      {r.renewals.length > 0 && (
        <div className="space-y-1">
          {r.renewals.map((x) => (
            <div key={x.accountId} className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
              <CalendarClock className="size-4 shrink-0 text-warning" />
              <ProviderIcon provider={x.provider} size="xs" />
              <span dir="auto" className="min-w-0 flex-1 truncate">{x.days === 0 ? t("usage.renewsToday", { name: x.label }) : t("usage.renewsIn", { name: x.label, count: x.days })}</span>
            </div>
          ))}
        </div>
      )}
      {r.accounts.length === 0 ? <p className="text-sm text-fg-muted">{t("usage.noAccounts")}</p> : <div className="grid gap-3 sm:grid-cols-2">{r.accounts.map((a) => <AccountCard key={a.id} a={a} />)}</div>}
      <Daily daily={r.daily} />
      {isAdmin && r.users.length > 0 && (
        <Card className="p-4">
          <h3 className="mb-2 text-sm font-medium">{t("usage.people")}</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-fg-muted"><th className="py-1 text-start font-normal">{t("usage.person")}</th><th className="py-1 text-end font-normal">{t("usage.week")}</th><th className="py-1 text-end font-normal">{t("usage.month")}</th></tr></thead>
            <tbody>{r.users.map((u) => <tr key={u.id} className="border-t border-border"><td dir="auto" className="py-1.5">{u.name}</td><td className="py-1.5 text-end tabular-nums">{u.runs7d}</td><td className="py-1.5 text-end tabular-nums text-fg-muted">{u.runs30d}</td></tr>)}</tbody>
          </table>
        </Card>
      )}
      {r.failures.length > 0 && (
        <Card className="p-4">
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium"><AlertTriangle className="size-4 text-danger" /> {t("usage.failures")}</h3>
          <ul className="divide-y divide-border text-sm">
            {r.failures.map((f) => (
              <li key={f.runId} className="flex items-start gap-2 py-2">
                {f.provider && <ProviderIcon provider={f.provider} size="xs" className="mt-0.5" />}
                <div className="min-w-0 flex-1">
                  {f.personal
                    ? <Link to="/chat/$chatId" params={{ chatId: f.chatId }} dir="auto" className="block truncate hover:underline">{f.chatTitle}</Link>
                    : <Link to="/projects/$projectId/chat/$chatId" params={{ projectId: f.projectId, chatId: f.chatId }} dir="auto" className="block truncate hover:underline">{f.chatTitle}</Link>}
                  {f.message && <p dir="auto" className="line-clamp-2 text-xs text-fg-muted">{f.message}</p>}
                </div>
                <time className="shrink-0 text-xs text-fg-muted">{new Date(f.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</time>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function AccountCard({ a }: { a: AccountUsage }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const save = useMutation({ mutationFn: (d: string | null) => usageApi.setRenewal(a.id, d), onSuccess: () => void qc.invalidateQueries({ queryKey: ["usage"] }) });
  const l = a.limits;
  return (
    <Card className={cn("space-y-3 p-4", l?.limited && "border-danger/50")}>
      <div className="flex items-center gap-2">
        <ProviderIcon provider={a.provider} size="sm" />
        <div className="min-w-0 flex-1">
          <div dir="auto" className="truncate text-sm font-medium">{a.label}</div>
          <div className="text-[11px] text-fg-muted">{a.shared ? t("usage.sharedBy", { name: a.ownerName }) : a.ownerName}</div>
        </div>
        {l?.limited && <span className="rounded bg-danger/15 px-1.5 py-0.5 text-[11px] text-danger">{t("usage.limited")}</span>}
      </div>
      {l ? (
        <div className="space-y-2">
          <Bar label={t("usage.fiveHour")} w={l.fiveHour} />
          <Bar label={t("usage.weekly")} w={l.weekly} />
          <p className="text-[10px] text-fg-muted">{t("usage.reportedAt", { at: new Date(l.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) })}</p>
        </div>
      ) : <p className="text-xs text-fg-muted">{t("usage.noLimits")}</p>}
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label={t("usage.runs5h")} v={a.runs5h} />
        <Stat label={t("usage.runs7d")} v={a.runs7d} />
        <Stat label={a.cost7d > 0 ? t("usage.cost7d") : t("usage.tokens7d")} v={a.cost7d > 0 ? `$${a.cost7d.toFixed(2)}` : compact(a.tokens7d)} />
      </div>
      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <CalendarClock className="size-3.5" /> {t("usage.renewsAt")}
        <input type="date" value={a.renewsAt ?? ""} onChange={(e) => save.mutate(e.target.value || null)} className="ms-auto rounded-md border border-border bg-bg px-2 py-1 text-xs text-fg" />
      </label>
    </Card>
  );
}

function Bar({ label, w }: { label: string; w: LimitWindow | null | undefined }) {
  const { t } = useTranslation();
  const pct = w ? Math.round(Math.min(1, Math.max(0, w.used)) * 100) : null;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-xs">
        <span>{label}</span>
        <span className="tabular-nums text-fg-muted">{pct === null ? "—" : `${pct}%`}{w?.resetsAt ? ` · ${t("usage.resets", { at: resetLabel(w.resetsAt) })}` : ""}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-surface-2">
        {pct !== null && <div className={cn("h-full rounded-full", pct >= 90 ? "bg-danger" : pct >= 70 ? "bg-warning" : "bg-success")} style={{ width: `${Math.max(2, pct)}%` }} />}
      </div>
    </div>
  );
}

function Stat({ label, v }: { label: string; v: string | number }) {
  return <div className="rounded-md bg-surface-2 px-1 py-1.5"><div className="text-sm font-medium tabular-nums">{v}</div><div className="text-[10px] text-fg-muted">{label}</div></div>;
}

/** Runs per day for the last two weeks, stacked by provider. */
function Daily({ daily }: { daily: UsageReport["daily"] }) {
  const { t } = useTranslation();
  const days = Array.from({ length: 14 }, (_, i) => new Date(Date.now() - (13 - i) * 86_400_000).toISOString().slice(0, 10));
  const providers = [...new Set(daily.map((d) => d.provider))];
  const total = (day: string) => daily.filter((d) => d.day === day).reduce((n, d) => n + d.runs, 0);
  const max = Math.max(1, ...days.map(total));
  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h3 className="flex-1 text-sm font-medium">{t("usage.daily")}</h3>
        {providers.map((p) => <span key={p} className="flex items-center gap-1 text-[11px] text-fg-muted"><span className="size-2 rounded-full" style={{ background: COLORS[p] ?? "#888" }} />{p}</span>)}
      </div>
      <div className="flex h-28 items-end gap-1" dir="ltr">
        {days.map((day) => (
          <div key={day} className="flex h-full flex-1 flex-col justify-end" title={`${day}: ${total(day)}`}>
            {providers.map((p) => {
              const n = daily.find((d) => d.day === day && d.provider === p)?.runs ?? 0;
              return n ? <div key={p} style={{ height: `${(n / max) * 100}%`, background: COLORS[p] ?? "#888" }} className="w-full first:rounded-t-sm" /> : null;
            })}
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-fg-muted" dir="ltr"><span>{days[0]!.slice(5)}</span><span>{days[13]!.slice(5)}</span></div>
    </Card>
  );
}

const compact = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n));
function resetLabel(ms: number) {
  const mins = Math.round((ms - Date.now()) / 60_000);
  if (mins <= 0) return "—";
  if (mins < 60) return `${mins}m`;
  if (mins < 48 * 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return new Date(ms).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}
